/**
 * /my-week — the crew member's personal agenda (Crew UX.2).
 *
 * A read-only, day-grouped list of my upcoming assignment blocks (today + the
 * next 6 days): when, where, which role, which truck. Tapping a row opens the
 * task detail. Distinct from the supervisor dispatch/schedule grids — this is
 * just "what's on my plate."
 */
import { useState } from 'react';
import { addDays, format, parseISO } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MapPin,
  Star,
  Truck,
} from 'lucide-react';
import type { CrewScheduleDay, CrewScheduleItem } from '@vari-crew/schemas';
import { useCrewSchedule } from '@/api/crew-today';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { CrewViewPicker } from '@/components/crew/CrewViewPicker';
import { CrewProjectCard } from '@/components/crew/CrewProjectCard';
import { CrewPickupCard } from '@/components/crew/CrewPickupCard';
import { CrewAvatars } from '@/components/ui/CrewAvatars';
import { cn } from '@/lib/cn';

const TYPE_LABEL: Record<CrewScheduleItem['type'], string> = {
  pickup: 'Pickup',
  install: 'Install',
  punch: 'Punch',
  labor: 'Labor',
};

function timeWindow(item: CrewScheduleItem): string {
  if (!item.scheduled_start) return 'Unscheduled';
  const start = format(parseISO(item.scheduled_start), 'h:mma').toLowerCase();
  if (!item.scheduled_end) return start;
  return `${start}–${format(parseISO(item.scheduled_end), 'h:mma').toLowerCase()}`;
}

export function MyWeek() {
  // 0 = the default rolling window (today + next 6 days, server-decided); ±n
  // pages by 7-day windows so the crew can look back / ahead (Crew UX.2).
  const [weekOffset, setWeekOffset] = useState(0);
  const [searchParams] = useSearchParams();
  const { data: me } = useCurrentUser();
  const isManager = me?.role === 'supervisor' || me?.role === 'admin';
  // Supervisors/admins can view another crew member's week (?crew_member_id).
  const viewAsId = isManager ? (searchParams.get('crew_member_id') ?? undefined) : undefined;
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const from = format(addDays(new Date(), weekOffset * 7), 'yyyy-MM-dd');
  const to = format(addDays(new Date(), weekOffset * 7 + 6), 'yyyy-MM-dd');
  const scheduleParams: { from?: string; to?: string; crewMemberId?: string } = {};
  if (weekOffset !== 0) {
    scheduleParams.from = from;
    scheduleParams.to = to;
  }
  if (viewAsId) scheduleParams.crewMemberId = viewAsId;
  const { data, isLoading, error } = useCrewSchedule(
    Object.keys(scheduleParams).length ? scheduleParams : undefined,
  );
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="h-full grid place-items-center bg-fill-soft">
        <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="h-full grid place-items-center p-6 text-status-blocked-fg text-sm">
        {(error as Error).message}
      </div>
    );
  }

  // Tasks + projects worked directly (D-3/A3) both count as assignments.
  const totalItems =
    data?.days.reduce((n, d) => n + d.items.length + d.projects.length + d.pickups.length, 0) ?? 0;

  return (
    <div className="min-h-full bg-fill-soft pb-20 lg:pb-8">
      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        {isManager && <CrewViewPicker />}
        <header>
          <div className="text-[10px] uppercase tracking-widest text-ink-faint flex items-center gap-1">
            <CalendarDays className="w-3 h-3" /> {viewAsId ? 'Crew view' : 'My week'}
          </div>
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <h1 className="text-2xl font-semibold">
              {weekOffset === 0 ? 'Upcoming' : 'Schedule'}
            </h1>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setWeekOffset((o) => o - 1)}
                aria-label="Previous week"
                className="rounded-md p-1.5 text-ink-faint hover:text-ink hover:bg-fill-soft transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              {weekOffset !== 0 && (
                <button
                  type="button"
                  onClick={() => setWeekOffset(0)}
                  className="rounded-md px-2 py-1 text-xs font-medium text-accent hover:bg-fill-soft transition-colors"
                >
                  This week
                </button>
              )}
              <button
                type="button"
                onClick={() => setWeekOffset((o) => o + 1)}
                aria-label="Next week"
                className="rounded-md p-1.5 text-ink-faint hover:text-ink hover:bg-fill-soft transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
          {data && (
            <p className="text-sm text-ink-dim">
              {format(parseISO(data.from), 'EEE MMM d')} –{' '}
              {format(parseISO(data.to), 'EEE MMM d')} ·{' '}
              {totalItems === 0
                ? 'nothing scheduled'
                : `${totalItems} assignment${totalItems === 1 ? '' : 's'}`}
            </p>
          )}
        </header>

        {isManager && !viewAsId ? (
          <div className="rounded-2xl border border-line bg-bg p-6 text-center text-sm text-ink-dim">
            Pick a crew member to see their week.
          </div>
        ) : (
          data?.days.map((day) => (
            <DaySection
              key={day.date}
              day={day}
              isToday={day.date === todayStr}
              onOpen={(id) => navigate(`/today/tasks/${id}`)}
              onOpenProject={(id) => navigate(`/today/projects/${id}`)}
              onOpenPickup={(id) => navigate(`/today/pickups/${id}`)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function DaySection({
  day,
  isToday,
  onOpen,
  onOpenProject,
  onOpenPickup,
}: {
  day: CrewScheduleDay;
  isToday: boolean;
  onOpen: (taskId: string) => void;
  onOpenProject: (projectId: string) => void;
  onOpenPickup: (pickupId: string) => void;
}) {
  const d = parseISO(day.date);
  return (
    <section className="space-y-1.5">
      <div className="flex items-baseline gap-2 px-1">
        <h2 className="text-sm font-semibold">
          {isToday ? 'Today' : format(d, 'EEEE')}
        </h2>
        <span className="text-xs text-ink-faint">{format(d, 'MMM d')}</span>
      </div>
      {/* Projects worked directly that day (D-3/A3) sit above the task items. */}
      {day.projects.length > 0 && (
        <ul className="space-y-1.5">
          {day.projects.map((p) => (
            <li key={p.block_id}>
              <CrewProjectCard
                project={p}
                expanded={false}
                canAct={false}
                onOpen={() => onOpenProject(p.project_id)}
              />
            </li>
          ))}
        </ul>
      )}
      {/* Pickups that day (workstream C), beside the projects. */}
      {day.pickups.length > 0 && (
        <ul className="space-y-1.5">
          {day.pickups.map((p) => (
            <li key={p.block_id}>
              <CrewPickupCard pickup={p} expanded={false} onOpen={() => onOpenPickup(p.pickup_id)} />
            </li>
          ))}
        </ul>
      )}
      {day.items.length === 0 ? (
        day.projects.length === 0 && day.pickups.length === 0 ? (
          <div className="text-xs text-ink-faint px-1 py-2">Nothing scheduled</div>
        ) : null
      ) : (
        <ul className="space-y-1.5">
          {day.items.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => onOpen(item.id)}
                className="w-full text-left rounded-lg border border-line bg-bg p-3 hover:border-ink-faint transition-colors flex items-start gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold tabular-nums">
                      {timeWindow(item)}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded border border-line text-ink-dim">
                      {TYPE_LABEL[item.type]}
                    </span>
                    {item.status !== 'scheduled' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded border border-line text-ink-dim capitalize">
                        {item.status.replace(/_/g, ' ')}
                      </span>
                    )}
                    {item.is_lead && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[10px] text-amber-700"
                        title="You lead this block"
                      >
                        <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                        Lead
                      </span>
                    )}
                  </div>
                  <div className="text-sm mt-0.5 truncate">
                    {item.project_client_name} · {item.title}
                  </div>
                  {item.crew.length > 0 && (
                    <CrewAvatars
                      crew={item.crew}
                      withNames
                      className="mt-1.5"
                      nameClass="text-ink-faint"
                    />
                  )}
                  <div className="mt-1 flex items-center gap-3 text-xs text-ink-faint">
                    {item.site_short_address && (
                      <span className="inline-flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate">{item.site_short_address}</span>
                      </span>
                    )}
                    {item.truck_label && (
                      <span className="inline-flex items-center gap-1 shrink-0">
                        <Truck className="w-3 h-3" />
                        {item.truck_label}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight
                  className={cn('w-4 h-4 text-ink-faint mt-0.5 shrink-0')}
                />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
